import { apiClient } from "./client";
import type {
    AdminAccessResponse,
    AdminPermissionAuditResponse,
    AdminStaffDirectoryResponse,
    AdminStaffLookupResponse,
} from "./types";

export async function fetchAdminAccessDataset() {
    const response = await apiClient.get<AdminAccessResponse>("/admin/access", {
        timeout: 60_000,
    });
    return response.data;
}

export async function fetchAdminStaffDirectory() {
    const response = await apiClient.get<AdminStaffDirectoryResponse>("/admin/staff-directory", {
        timeout: 180_000,
    });
    return response.data;
}

export async function fetchAdminStaffLookup(params: { email?: string; name?: string; staffId?: number }) {
    const response = await apiClient.get<AdminStaffLookupResponse>("/admin/staff-lookup", {
        params: {
            email: params.email || undefined,
            name: params.name || undefined,
            staffId: params.staffId && params.staffId > 0 ? params.staffId : undefined,
        },
        timeout: 30_000,
    });
    return response.data;
}

export async function createAdminUser(payload: Record<string, unknown>) {
    const response = await apiClient.post<AdminAccessResponse>("/admin/users", payload);
    return response.data;
}

export async function updateAdminUser(userId: number, payload: Record<string, unknown>) {
    const response = await apiClient.patch<AdminAccessResponse>(`/admin/users/${userId}`, payload);
    return response.data;
}

export async function deleteAdminUser(userId: number) {
    const response = await apiClient.delete<AdminAccessResponse>(`/admin/users/${userId}`);
    return response.data;
}

export async function createAdminRole(payload: Record<string, unknown>) {
    const response = await apiClient.post<AdminAccessResponse>("/admin/roles", payload);
    return response.data;
}

export async function updateAdminRole(roleId: number, payload: Record<string, unknown>) {
    const response = await apiClient.patch<AdminAccessResponse>(`/admin/roles/${roleId}`, payload);
    return response.data;
}

export async function updateAdminRolePermissions(roleId: number, permissionIds: number[]) {
    const response = await apiClient.put<AdminAccessResponse>(`/admin/roles/${roleId}/permissions`, {
        permission_ids: permissionIds,
    });
    return response.data;
}

export async function updateAdminUserPermissionOverrides(
    userId: number,
    overrides: Array<{ permission_id: number; effect: "grant" | "deny" }>,
) {
    const response = await apiClient.put<AdminAccessResponse>(`/admin/users/${userId}/permission-overrides`, {
        overrides,
    });
    return response.data;
}

export async function fetchAdminPermissionAudit(
    targetType: "user" | "role",
    targetId: number,
    limit = 30,
) {
    const response = await apiClient.get<AdminPermissionAuditResponse>(
        `/admin/access-audit/${targetType}/${targetId}`,
        { params: { limit } },
    );
    return response.data;
}
